import React, {useEffect, useState} from "react";
import {
  FormAutocomplete,
  LabeledAutocomplete,
  formClasses,
  labelClasses,
} from "../styles/AutocompleteStyles";
import {TextField} from "@mui/material";
import {useLazyQuery} from "@apollo/client";

import useUrlParams from "../customHooks/useUrlParams";
import {FETCH_MORE_DATA, ROWS_PER_PAGE} from "../utils/constants";
import {OrderDirection} from "../utils/commonQueryTypes";
import {getModifiedScrollHeight} from "../utils/UtilFunctions";
import {GetSubsidiaries} from "../components/modules/asset/masters/subsidary/queries/queries";
import {
  AtsSubsidiaryEdge,
  AtsSubsidiaryOrderField,
  GetSubsidiariesResult,
  GetSubsidiariesVars,
} from "../components/modules/asset/masters/subsidary/queries/types";
import useLoggedInUserDetails from "../customHooks/useLoggedInUserDetails";
interface Props {
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  onChange: (newValue: AtsSubsidiaryEdge) => void;
  value: number;
  label: string;
  autoFocus?: boolean;
  companyID?: number;
  placeholder?: string;
}
const SubsidiarySelect = ({
  onChange,
  onKeyDown,
  autoFocus,
  value,
  label,
  companyID,
  placeholder,
}: Props) => {
  const {customer_id, company_id} = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [subsidiaries, setSubsidiaries] = useState<AtsSubsidiaryEdge[]>([]);
  const [autoValue, setAutoValue] = useState<AtsSubsidiaryEdge | null>(null);

  const [GetSubsidiary, {data, loading, fetchMore}] = useLazyQuery<
    GetSubsidiariesResult,
    GetSubsidiariesVars
  >(GetSubsidiaries);

  const {user_details} = useLoggedInUserDetails();
  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetSubsidiaries.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = subsidiaries.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setSubsidiaries(updatedNewData);
      } else {
        setSubsidiaries(newData);
      }
      setEndCursor(data.GetSubsidiaries.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id) {
          GetSubsidiary({
            variables: {
              after: null,
              customerID: customer_id,
              companyID: companyID ? companyID : 0,
              empID: user_details.id,
              first: ROWS_PER_PAGE,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsSubsidiaryOrderField.NAME,
              },

              name: searchData,
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [
    customer_id,
    company_id,
    GetSubsidiary,
    user_details.id,
    searchData,
    companyID,
  ]);

  useEffect(() => {
    if (value) {
      const foundValue = subsidiaries.find(
        ({node}) => Number(node.id) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, subsidiaries]);
  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, {fetchMoreResult}) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetSubsidiaries.edges;
            const pageInfo = fetchMoreResult.GetSubsidiaries.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetSubsidiaries.edges.filter(
              ({node: {id}}) =>
                newEdges.findIndex(({node: {id: newId}}) => newId === id) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetSubsidiaries: {
                edges: [...subsidiaries, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetSubsidiaries.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete =
    label.length > 0 ? LabeledAutocomplete : FormAutocomplete;

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={subsidiaries}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      getOptionLabel={(option) => (option as AtsSubsidiaryEdge).node.name}
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        onChange(newValue as AtsSubsidiaryEdge);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          autoFocus={autoFocus}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default SubsidiarySelect;
